import { AfterContentInit, Component, Input, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { User, SignatureFormat, FontEnum, UserSignatureInput, SignaturePadEnum } from '@app/models';
import { SignaturePadComponent } from '@app/shared/signature-pad/signature-pad.component';
import { InitialsNamePipe } from '@app/shared/pipes/initials-name.pipe';
import { SignatureNamePipe } from '@app/shared/pipes/signature-name.pipe';

export type ModalResult = { signature: UserSignatureInput; initials: UserSignatureInput };
export interface ModalPublicProperties {
  user: User;
}

@Component({
  selector: 'app-edit-signature-modal',
  templateUrl: './edit-signature-modal.component.html',
  styleUrls: ['./edit-signature-modal.component.scss'],
  standalone: false
})
export class EditSignatureModalComponent implements ModalPublicProperties, AfterContentInit {
  @ViewChild('signaturePad', { static: false }) private signaturePad: SignaturePadComponent;
  @Input() user: User;
  @Input() canSubmitForm: boolean;
  signatureNamePipe = new SignatureNamePipe();
  initialsPipe = new InitialsNamePipe();
  SignaturePadEnum = SignaturePadEnum;
  form!: UntypedFormGroup;
  readonly SignatureFormat = SignatureFormat;
  readonly fonts = {
    [FontEnum.Autograf]: 'font_enum_autograf',
    [FontEnum.Fathur]: 'font_enum_farthur',
    [FontEnum.Robertson]: 'font_enum_robertson'
  };

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: UntypedFormBuilder
  ) {
    this.form = this.formBuilder.group({
      signature: this.formBuilder.group({
        font: FontEnum.Autograf,
        format: SignatureFormat.Handwriting,
        text: '',
        file_image: null,
        file_draw: null
      }),
      initials: this.formBuilder.group({
        font: FontEnum.Autograf,
        format: SignatureFormat.Handwriting,
        text: '',
        file_image: null,
        file_draw: null
      })
    });
  }

  ngAfterContentInit() {
    this.form.get('signature')?.patchValue({
      font: this.user.settings.font,
      format: this.user.settings.format,
      text: this.user.settings.signature_text || this.signatureNamePipe.transform(this.user.name)
    });
    this.form.get('initials')?.patchValue({
      font: this.user.settings.font,
      format: this.user.settings.format,
      text: this.user.settings.initials_text || this.initialsPipe.transform(this.user.name)
    });
  }

  submit() {
    if (!this.canSubmitForm) {
      return;
    }

    const signatureData = this.form.get('signature')?.value;
    const initialsData = this.form.get('initials')?.value;

    const signatureFormat = signatureData.format;
    const initialsFormat = initialsData.format;

    let signatureFile: File | null = null;
    let initialsFile: File | null = null;

    if ([SignatureFormat.Image, SignatureFormat.Draw].includes(signatureFormat)) {
      signatureFile = this.signaturePad.toImageFile(SignaturePadEnum.Signature);
    }

    if ([SignatureFormat.Image, SignatureFormat.Draw].includes(initialsFormat)) {
      initialsFile = this.signaturePad.toImageFile(SignaturePadEnum.Initials);
    }

    const buildSignatureInput = (data: UserSignatureInput, file: File | null): UserSignatureInput => {
      const input = { ...data };
      if (file) {
        if (data.format === SignatureFormat.Draw) {
          input.file_draw = file;
        } else if (data.format === SignatureFormat.Image) {
          input.file_image = file;
        }
      }
      return input;
    };

    const result: ModalResult = {
      signature: buildSignatureInput(signatureData, signatureFile),
      initials: buildSignatureInput(initialsData, initialsFile)
    };

    this.modal.close(result);
  }
}
